import { z } from 'zod';

export const llmMessageSchema = z.object({
  type: z.string(),
  run_id: z.string(),
  content: z.string(),
  timestamp: z.string().datetime(),
  hash: z.string(),
  workflow_id: z.string(),
  task_id: z.string(),
  index: z.number(),
});

export const taskEventSchema = z
  .object({
    event_id: z.string(),
    updated_at: z.string().datetime(),
    explanation: z.string().optional(),
    action: z.string().optional(),
    type: z.string(),
    tool_name: z.string().optional(),
    tool_response: z.string().optional(),
    email_content: z.string().optional(),
    tool_input: z.array(z.any()).optional(),
    llm_messages: z.array(llmMessageSchema).nullable().optional().default([]),
  })
  .passthrough();

export const confirmationOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const checkpointSchema = z
  .object({
    checkpoint_id: z.string().uuid(),
    workflow_id: z.string().uuid(),
    task_id: z.string().uuid(),
    should_continue: z.boolean().nullable(),
    confirmation_options: z.array(confirmationOptionSchema).optional(),
    confirmation_action: z.string().optional(),
    type: z.enum([
      'Checkpoint',
      'UserConfirmationCheckpoint',
      'GetTradeEmailResponseTask',
      'EmailCheckpoint',
    ]),
    email_input: z
      .object({
        type: z.string(),
        content: z.string(),
        subject: z.string(),
        email_id: z.string(),
        to_address: z.string(),
        from_address: z.string(),
        conversation_id: z.string(),
      })
      .passthrough()
      .nullable()
      .optional(),
    to_address: z.string().optional(),
    conversation_id: z.string().optional(),
  })
  .passthrough();

export const TASK_NAMES = [
  'ComposeEmailTask',
  'ConfirmEmailTask',
  'EmailCategorizerTask',
  'TradeCheckerTask',
  'ConfirmAndBookOrderTask',
  'GetTradeEmailResponseTask',
  'TerminalTask',
] as const;

export type TaskName = (typeof TASK_NAMES)[number];

export const tradeDetailSchema = z.object({
  quantity: z.number(),
  isin: z.string(),
  price: z.number(),
  action: z.enum(['proceed', 'not_proceed']),
});

export const taskInputSchema = z
  .object({
    analysis: z.string().optional(),
    to_address: z.string().optional(),
    from_address: z.string().optional(),
    content: z.string().optional(),
    email_id: z.string().optional(),
    subject: z.string().optional(),
    conversation_id: z.string().optional(),
    type: z.string().optional(),
    event_id: z.string().optional(),
    updated_at: z.string().optional(),
    explanation: z.string().optional(),
    action: z.string().optional(),
    // Individual trade fields for backward compatibility
    quantity: z.number().optional(),
    isin: z.string().optional(),
    price: z.number().optional(),
    // New field for list of trade details
    details: z.array(tradeDetailSchema).optional(),
  })
  .passthrough();

export const taskStateSchema = z
  .object({
    output: z.record(z.any()).nullable(),
    task_input: taskInputSchema,
    trigger_outputs: z.record(z.any()).nullable().optional(),
    task_id: z.string().uuid(),
    events: z.record(taskEventSchema),
    created_at: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid datetime',
    }),
    checkpoint: checkpointSchema.nullable().optional(),
    status: z.enum([
      'completed',
      'failed',
      'running',
      'pending',
      'not-started',
    ]),
  })
  .passthrough();

export const WORKFLOW_STATUS = [
  'not-started',
  'starting',
  'running',
  'completed',
  'pending',
  'failed',
] as const;

export const workflowSchema = z
  .object({
    workflow_id: z.string(),
    task_states: z.record(taskStateSchema),
    status: z.enum(WORKFLOW_STATUS),
    priority: z.enum(['low', 'high']),
    started_at: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid datetime',
    }),
    updated_at: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'Invalid datetime',
    }),
    title: z.string().nullable(),
    data: z
      .object({
        to_address: z.string().optional(),
        from_address: z.string().optional(),
        content: z.string().optional(),
        email_id: z.string().optional(),
        subject: z.string().optional(),
        conversation_id: z.string().optional(),
        type: z.string().optional(),
      })
      .passthrough(),
  })
  .passthrough();

// Agent events schemas and types
export const agentEventNameSchema = z.enum([
  'thinking_delta',
  'text_delta',
  'thinking_start',
  'text_start',
  'thinking_end',
  'tool_call',
  'tool_call_delta',
  'response',
]);

export const agentEventSchema = z.object({
  event_name: agentEventNameSchema,
  data: z.union([z.string(), z.record(z.unknown())]),
  timestamp: z.string().datetime(),
  uuid_run: z.string(),
  index: z.number(),
  task_id: z.string(),
  workflow_id: z.string(),
});

// Create a discriminated union schema for better type safety
export const workflowStateResponseSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('workflow_state'),
    content: workflowSchema,
  }),
  z.object({
    type: z.literal('llm_event'),
    content: agentEventSchema,
  }),
]);

export const singleWorkflowStateSchema = z.object({
  workflow: workflowSchema.nullable(),
  currentAgentEvent: agentEventSchema.nullable(),
  agentEvents: z.array(agentEventSchema),
  isLoading: z.boolean(),
  error: z.string().nullable(),
});

// Type exports
export type Workflow = z.infer<typeof workflowSchema>;
export type WorkflowStatus = z.infer<typeof workflowSchema>['status'];
export type WorkflowPriority = z.infer<typeof workflowSchema>['priority'];
export type TaskEvent = z.infer<typeof taskEventSchema>;
export type TaskState = z.infer<typeof taskStateSchema>;
export type AgentEventName = z.infer<typeof agentEventNameSchema>;
export type AgentEvent = z.infer<typeof agentEventSchema>;
export type WorkflowStateResponse = z.infer<typeof workflowStateResponseSchema>;
export type SingleWorkflowState = z.infer<typeof singleWorkflowStateSchema>;
export type LlmMessage = z.infer<typeof llmMessageSchema>;
